import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { ProductCard } from '@/components/products/product-card';

export function FeaturedProducts() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  useEffect(() => {
    fetchFeaturedProducts();
  }, []);
  
  const fetchFeaturedProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(8);
      
      if (fetchError) {
        console.error('Supabase query error:', fetchError);
        throw new Error(`Failed to fetch featured products: ${fetchError.message}`);
      }
      
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching featured products:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch featured products'));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-white">
      <div className="max-w-2xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:max-w-7xl lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {t('home.featuredProducts.title')}
          </h2>
          <Link 
            to="/products" 
            className="text-primary hover:text-primary-dark text-sm font-medium"
          >
            {t('common.viewAll')} →
          </Link>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-2 gap-y-8 gap-x-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-x-6">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse h-full flex flex-col"
              >
                <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-200" />
                <div className="mt-4 space-y-2 flex-grow">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
                <div className="mt-4 h-8 bg-gray-200 rounded w-full" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">{t('common.error')}</p>
            <p className="text-sm text-gray-500 mt-2">{error.message}</p>
            <button 
              onClick={fetchFeaturedProducts}
              className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              {t('common.retry')}
            </button>
          </div>
        ) : !products.length ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('common.noResults')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-y-8 gap-x-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-x-6">
            {products.map((product) => (
              <div key={product.id} className="h-full flex flex-col">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}